import { useMemo, useState } from "react";
import { ScrollView, Text, View } from "react-native";
import {
  AlertCircle,
  Beaker,
  BookOpen,
  CalendarDays,
  DollarSign,
  FileText,
  Heart,
  Info,
  TestTube2,
  Timer,
  Wrench,
  X,
} from "lucide-react-native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import {
  getBlocksByKind,
  getCollectionLabel,
  getRelatedBlocks,
  type ContentBlock,
  type TestBody,
} from "@src/packages/content";
import { useTranslations } from "@src/packages/i18n";
import type { AccessStackParamList } from "../../types";
import { useSmartBack } from "../../hooks/useSmartBack";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import {
  GLASS_CARD_STYLE,
  SCREEN_PADDING_TOP,
} from "@louise/moon-ui/theme/tokens";
import { IconButton, SectionTitle } from "@louise/moon-ui";
import FactSheetHeader from "../../components/FactSheetHeader";
import FactSheetTabControl, {
  type FactSheetTab,
} from "../../components/FactSheetTabControl";
import AccessGridCard, {
  AccessGridCardPill,
} from "../../components/access/AccessGridCard";
import EvidenceSourceRows from "../../components/EvidenceSourceRows";
import EvidenceLevelModal from "../../components/EvidenceLevelModal";
import { getTestIllustration } from "../../components/access/testIllustrations";
import { EMBER } from "../../../onboarding/theme";

type Props = NativeStackScreenProps<
  AccessStackParamList,
  "EndometrialAssayComparison"
>;
type AssayTab = "overview" | "results" | "biology" | "testing";
type ResultType = NonNullable<TestBody["resultTypes"]>[number];

function getTestBlock(id: string): ContentBlock<"test"> | null {
  const candidateIds = id.startsWith("test-") ? [id] : [id, `test-${id}`];
  return (
    getBlocksByKind("test").find((block) =>
      candidateIds.some((candidateId) => block.id === candidateId),
    ) ?? null
  );
}

function BodyCopy({ children }: { children: string }) {
  return (
    <Text
      className="font-dm-sans"
      style={{ fontSize: 14, lineHeight: 22, color: EMBER.colors.textLight }}
    >
      {children}
    </Text>
  );
}

function QuickFactGrid({
  items,
}: {
  items: NonNullable<TestBody["quickFacts"]>;
}) {
  return (
    <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
      {items.map((item, index) => (
        <View
          key={`${item.kind}-${index}`}
          style={{
            width: "48%",
            minHeight: 68,
            borderRadius: 10,
            borderWidth: 1,
            borderColor: "rgba(0,0,0,0.06)",
            backgroundColor: "rgba(255,255,255,0.28)",
            paddingHorizontal: 12,
            paddingVertical: 10,
          }}
        >
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 10,
              fontWeight: "700",
              color: EMBER.colors.textFaint,
              textTransform: "uppercase",
            }}
          >
            {item.label ?? item.kind}
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              marginTop: 5,
              fontSize: 13,
              lineHeight: 18,
              fontWeight: "600",
              color: EMBER.colors.textMid,
            }}
          >
            {item.value}
          </Text>
        </View>
      ))}
    </View>
  );
}

function ResultTypeCard({ item }: { item: ResultType }) {
  return (
    <View
      style={{
        width: "48%",
        minHeight: 130,
        borderRadius: 10,
        borderWidth: 1,
        borderColor: "rgba(0,0,0,0.06)",
        backgroundColor: "rgba(255,255,255,0.28)",
        paddingHorizontal: 12,
        paddingVertical: 11,
      }}
    >
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 14,
          lineHeight: 18,
          fontWeight: "700",
          color: EMBER.colors.text,
        }}
      >
        {item.label}
      </Text>
      <Text
        className="font-dm-sans"
        style={{
          marginTop: 6,
          fontSize: 12,
          lineHeight: 17,
          color: EMBER.colors.textLight,
        }}
      >
        {item.meaning}
      </Text>
    </View>
  );
}

function ResultTypeList({ items }: { items: ResultType[] }) {
  return (
    <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
      {items.map((item) => (
        <ResultTypeCard key={item.label} item={item} />
      ))}
    </View>
  );
}

function CommercialTestCard({
  profile,
  onPress,
}: {
  profile: ContentBlock<"test">;
  onPress: () => void;
}) {
  const company = profile.body.commercialProductProfile?.company;
  const body = profile.body;
  const cost = body.costRange
    ? `$${body.costRange.low}–$${body.costRange.high}`
    : undefined;

  return (
    <View style={{ width: "48%" }}>
      <AccessGridCard
        title={profile.title}
        description={
          company ? `${company} · ${profile.subtitle}` : profile.subtitle
        }
        imageSource={getTestIllustration({
          testId: String(profile.id),
          category: profile.body.category,
        })}
        imageStyle={{ width: 66, height: 84 }}
        onPress={onPress}
        tags={
          <>
            <AccessGridCardPill
              label={getCollectionLabel(body)}
              icon={TestTube2}
            />
            {body.turnaroundTime ? (
              <AccessGridCardPill label={body.turnaroundTime} icon={Timer} />
            ) : null}
            {cost ? (
              <AccessGridCardPill label={cost} icon={DollarSign} />
            ) : null}
          </>
        }
      />
    </View>
  );
}

function BulletList({ items }: { items: string[] }) {
  return (
    <View style={{ gap: 8 }}>
      {items.map((item) => (
        <Text
          key={item}
          className="font-dm-sans"
          style={{
            fontSize: 13,
            lineHeight: 20,
            color: EMBER.colors.textLight,
          }}
        >
          • {item}
        </Text>
      ))}
    </View>
  );
}

export default function EndometrialAssayComparisonScreen({
  navigation,
  route,
}: Props) {
  const t = useTranslations();
  const [activeTab, setActiveTab] = useState<AssayTab>("overview");
  const [evidenceLevel, setEvidenceLevel] = useState<string | null>(null);
  const handleBack = useSmartBack({
    returnTab: route.params.returnTab,
    fallbackTab: "Access",
    fallbackScreen: "Testing",
  });
  const test = useMemo(() => getTestBlock(route.params.testId), [route.params]);
  const relatedCommercialTests = useMemo(() => {
    if (!test) return [];

    return getRelatedBlocks(test.id, "related-to")
      .filter(
        ({ relationship }) =>
          relationship.from === test.id && relationship.label === "Sold as",
      )
      .map(({ block }) => block)
      .filter(
        (block): block is ContentBlock<"test"> =>
          block.kind === "test" &&
          block.body.isCommercialProductProfile === true,
      );
  }, [test]);

  if (!test) return null;

  const body = test.body;
  const tabs: FactSheetTab<AssayTab>[] = [
    {
      value: "overview",
      label: t("care.lab_result.tab_overview"),
      shortLabel: t("care.lab_result.tab_overview_short"),
      icon: Info,
    },
    {
      value: "results",
      label: t("care.lab_result.tab_values"),
      shortLabel: t("care.lab_result.tab_values_short"),
      icon: FileText,
    },
    {
      value: "biology",
      label: t("care.lab_result.tab_biology"),
      shortLabel: t("care.lab_result.tab_biology_short"),
      icon: Beaker,
    },
    {
      value: "testing",
      label: t("access.testing.comparison.get_tested"),
      shortLabel: t("access.testing.comparison.get_tested_short"),
      icon: TestTube2,
    },
  ];
  const howItWorksSteps =
    body.howItWorksSteps ??
    body.howItWorks
      .split(/(?<=[.!?])\s+/)
      .map((step) => step.trim())
      .filter(Boolean);

  return (
    <GlassScreenLayout>
      <ScrollView
        contentContainerStyle={{
          paddingHorizontal: 16,
          paddingTop: SCREEN_PADDING_TOP,
          paddingBottom: 100,
          maxWidth: 560,
          width: "100%",
          alignSelf: "center",
        }}
        showsVerticalScrollIndicator={false}
      >
        <View
          style={{
            ...GLASS_CARD_STYLE,
            paddingHorizontal: 24,
            paddingTop: 24,
            paddingBottom: 32,
          }}
        >
          <View style={{ position: "absolute", top: 16, right: 16, zIndex: 2 }}>
            <IconButton
              onPress={handleBack}
              hitSlop={8}
              size={32}
              surface="soft"
              backgroundColor="rgba(0,0,0,0.05)"
              pressedBackgroundColor="rgba(0,0,0,0.05)"
              pressedOpacity={1}
              accessibilityLabel={t(
                "learn.article_actions.close_accessibility",
              )}
            >
              <X size={16} color={EMBER.colors.textLight} />
            </IconButton>
          </View>

          <FactSheetHeader
            title={test.title}
            subtitle={test.subtitle}
            imageSource={getTestIllustration({
              testId: String(test.id),
              category: body.category,
            })}
            imageSize={86}
            badges={[
              { label: t("learn.test_detail.category_biopsy") },
              ...(body.clinicRequired
                ? [{ label: t("learn.test_detail.badge_clinic_visit") }]
                : []),
            ]}
          />

          <FactSheetTabControl
            active={activeTab}
            onChange={setActiveTab}
            tabs={tabs}
          />

          {activeTab === "overview" ? (
            <View style={{ marginTop: 24, gap: 24 }}>
              <View style={{ gap: 10 }}>
                <SectionTitle icon={Heart}>
                  {t("learn.test_detail.section_what_it_measures")}
                </SectionTitle>
                <BodyCopy>{body.whatItMeasures}</BodyCopy>
              </View>

              {body.quickFacts?.length ? (
                <View style={{ gap: 10 }}>
                  <SectionTitle icon={Info}>
                    {t("access.testing.comparison.at_a_glance")}
                  </SectionTitle>
                  <QuickFactGrid items={body.quickFacts} />
                </View>
              ) : null}

              {body.resultTypes?.length ? (
                <View style={{ gap: 10 }}>
                  <SectionTitle icon={FileText}>
                    {t("learn.test_detail.section_possible_results")}
                  </SectionTitle>
                  <ResultTypeList items={body.resultTypes.slice(0, 4)} />
                </View>
              ) : null}

              {body.keyFindings?.length ? (
                <View style={{ gap: 10 }}>
                  <SectionTitle icon={AlertCircle}>
                    {t("learn.test_detail.section_why_it_matters")}
                  </SectionTitle>
                  <BulletList
                    items={body.keyFindings.map((item) => item.outcome)}
                  />
                </View>
              ) : null}
            </View>
          ) : null}

          {activeTab === "results" ? (
            <View style={{ marginTop: 24, gap: 20 }}>
              <View style={{ gap: 10 }}>
                <SectionTitle icon={FileText}>
                  {t("learn.test_detail.section_possible_results")}
                </SectionTitle>
                {body.resultTypes?.length ? (
                  <ResultTypeList items={body.resultTypes} />
                ) : (
                  <BodyCopy>
                    {t("learn.test_detail.no_result_types_available")}
                  </BodyCopy>
                )}
              </View>
              <View style={{ gap: 10 }}>
                <SectionTitle icon={AlertCircle}>
                  {t("learn.test_detail.section_considerations")}
                </SectionTitle>
                {body.expectations?.shouldNotExpect?.length ? (
                  <BulletList items={body.expectations.shouldNotExpect} />
                ) : null}
                {body.considerations?.length ? (
                  <BulletList items={body.considerations} />
                ) : null}
              </View>
            </View>
          ) : null}

          {activeTab === "biology" ? (
            <View style={{ marginTop: 24, gap: 24 }}>
              <View style={{ gap: 10 }}>
                <SectionTitle icon={Beaker}>
                  {t("care.lab_result.biological_basis")}
                </SectionTitle>
                <BodyCopy>{body.whatItMeasures}</BodyCopy>
              </View>
              <View style={{ gap: 10 }}>
                <SectionTitle icon={Wrench}>
                  {t("learn.test_detail.section_how_it_works")}
                </SectionTitle>
                <BodyCopy>{body.howItWorks}</BodyCopy>
                <BulletList items={howItWorksSteps} />
              </View>
              {body.cycleDay ? (
                <View style={{ gap: 10 }}>
                  <SectionTitle icon={CalendarDays}>
                    {t("learn.test_detail.section_when_to_take")}
                  </SectionTitle>
                  <BodyCopy>{body.cycleDay}</BodyCopy>
                </View>
              ) : null}
              {test.sources?.length ? (
                <View style={{ gap: 10 }}>
                  <SectionTitle icon={BookOpen}>
                    {t("learn.test_detail.section_evidence")}
                  </SectionTitle>
                  <EvidenceSourceRows
                    sources={test.sources}
                    onEvidenceLevelPress={setEvidenceLevel}
                  />
                </View>
              ) : null}
            </View>
          ) : null}

          {activeTab === "testing" ? (
            <View style={{ marginTop: 24, gap: 16 }}>
              <BodyCopy>
                {t("learn.test_detail.related_commercial_tests_description")}
              </BodyCopy>
              <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 12 }}>
                {relatedCommercialTests.map((profile) => (
                  <CommercialTestCard
                    key={profile.id}
                    profile={profile}
                    onPress={() =>
                      navigation.navigate("TestDetail", {
                        testId: String(profile.id),
                        returnTab: route.params.returnTab,
                      })
                    }
                  />
                ))}
              </View>
            </View>
          ) : null}

          <View
            style={{
              marginTop: 32,
              padding: 14,
              borderRadius: 12,
              backgroundColor: "rgba(0,0,0,0.03)",
            }}
          >
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 11,
                color: EMBER.colors.textFaint,
                lineHeight: 17,
                textAlign: "center",
              }}
            >
              {t("learn.disclaimer_medical_info")}
            </Text>
          </View>
        </View>
      </ScrollView>
      <EvidenceLevelModal
        level={evidenceLevel}
        visible={evidenceLevel !== null}
        onClose={() => setEvidenceLevel(null)}
      />
    </GlassScreenLayout>
  );
}
